/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomDataGridV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CustomPaginationV2 from "../pagination/CustomPaginationV2";
import { componentStyle } from "../../../../config/appTheme";
import CustomButtonV2 from "../button/CustomButtonV2";


function CustomDataGridV2({
  disableRowSelectionOnClick = true,
  rows,
  columns,
  hideHeader = false,
  isMultiLine = false,
  disableColumnResize = true,
  page,
  rowsPerPage,
  handlePageChange,
  totalItems = 0,
  showPagination = false,
  enableMore = false,
  handleMore,
  getRowHeight = () => { },
}) {
  const fontStyle = componentStyle.font.tableText;
  const [gridKey, setGridKey] = useState(0);
  useEffect(() => {
    const handleResize = () => {
      setGridKey((k) => k + 1); // force remount
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        ...componentStyle.scroll,
        ...fontStyle,
        "& .MuiDataGrid-row": {
          backgroundColor: "white", // all rows white
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#fff", // optional, ensures consistent white header
          ...fontStyle,
        },
        "& .MuiDataGrid-columnHeader:hover": {
          backgroundColor: "#F5F5F5", // header cell hover color
        },
        "& .MuiDataGrid-cell": {
          ...(isMultiLine && {
            py: 1,
          }),
          ...fontStyle,
          borderBottom: "1px solid #e0e0e0", // Add border to cells
        },
      }}
    >
      <DataGrid
        key={gridKey}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        localeText={{ toolbarDensity: "View" }}
        disableColumnResize={disableColumnResize}
        rows={rows || []}
        columns={columns || []}
        hideFooter
        disableColumnMenu
        getRowHeight={isMultiLine ? () => "auto" : undefined}
        sx={{
          flex: 1,
          minHeight: 0,
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            wordBreak: "break-word",
            alignItems: "flex-start",
            display: "flex",
            lineHeight: 1.4,

            py: 1,
            ...fontStyle,
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "#F5F5F5 !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            ...(hideHeader && { display: "none" }),
          },

          "& .MuiDataGrid-selectedRowCount": {
            display: "none",
          },
          // Focus cell
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
            backgroundColor: "#E0E0E0 !important",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-cell.Mui-focusVisible": {
            outline: "none",
          },
        }}
      />
      {showPagination && rows?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CustomPaginationV2
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            totalItems={totalItems}
          />
        </Box>
      )}
      {enableMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "right",
            p: 2,
          }}
        >
          <CustomButtonV2 btnName={"See More..."} handleOnClick={handleMore} />
        </Box>
      )}
    </Box>
  );
}

export default CustomDataGridV2;
